"use client";

import { cn } from "@ui/lib/utils";
import { CircleStop, EditIcon, RepeatIcon } from "lucide-react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";

import type { Sound } from "../types";

import { toggleIsPaused, toggleSoundLoop } from "../state/actions";
import { useSoundboard } from "../state/reducer";
import { useAudio } from "./AudioPlayer";
interface SoundBlock {
  id: string;
  looped: boolean;
  name: string;
  progress: number;
}

export const PlayerPanel = () => {
  const { dispatch, state } = useSoundboard();
  const { clearPlayingSounds } = useAudio();
  const t = useTranslations("ClientKeys.soundboard");
  const globalT = useTranslations("ClientKeys.Globals");

  // Memoize current preset lookup
  const currentPreset = useMemo(() => state.presets[state.activePresetId!], [state.presets, state.activePresetId]);

  const playingState: {
    blocks: SoundBlock[];
    loopCount: number;
    state: "blocks" | "editing" | "loop-select" | "placeholder";
  } = useMemo(() => {
    const blocks: SoundBlock[] = [];
    const playingSounds = Object.keys(state.playingSounds);
    for (let i = 0; i < playingSounds.length; i++) {
      const soundId = playingSounds[i];
      if (soundId) {
        const sound = currentPreset?.sounds.find(s => s.id === soundId) as Sound;
        blocks.push({
          id: soundId,
          name: sound?.name || "Untitled",
          progress:
            1 - (state.soundMetadata[soundId]?.currentTime ?? 0) / (state.soundMetadata[soundId]?.duration ?? 1),
          looped: !!sound?.isLooped,
        });
      }
    }
    return {
      blocks,
      loopCount: blocks.filter(b => b.looped).length,
      state: state.isInLoopEditMode
        ? "loop-select"
        : state.isInEditMode
          ? "editing"
          : blocks.length === 0
            ? "placeholder"
            : "blocks",
    };
  }, [state.isInLoopEditMode, state.isInEditMode, state.playingSounds, state.soundMetadata, currentPreset?.sounds]);

  const handleUnloop = useCallback(
    (soundId: string) => {
      dispatch(toggleSoundLoop({ soundId }));
    },
    [dispatch],
  );

  return (
    <div className='sb-display-panel relative rounded-md'>
      <div className='sb-border-inverse flex min-h-28 items-center gap-8 border-b p-12'>
        <span className='sb-text-shadow sb-text-shadow-white font-soundboard rounded-sm bg-overlay-light p-4 text-xs font-semibold uppercase text-inverse'>
          {currentPreset?.name}
        </span>
        {playingState.loopCount > 0 && (
          <span className='sb-color-red sb-text-shadow sb-text-shadow-red sb-bg-red font-soundboard rounded-sm p-4 text-xs font-semibold uppercase'>
            {t("display.looping_count", { count: playingState.loopCount })}
          </span>
        )}
        <div className='ml-auto flex items-center gap-8'>
          {currentPreset?.hasEdits && (
            <span className='sb-color-orange sb-text-shadow sb-text-shadow-orange sb-bg-orange-light font-soundboard rounded-sm p-4 text-xs font-semibold uppercase'>
              {t("display.unsaved")}
            </span>
          )}

          <button
            aria-label={t("display.clear_all_sounds")}
            disabled={playingState.blocks.length === 0}
            onClick={() => {
              clearPlayingSounds();
            }}
            title={t("display.clear_all_sounds")}
          >
            <CircleStop className='opacity-20 transition hover:opacity-60' style={{ color: "white" }} />
          </button>
          <button
            aria-label={state.isPaused ? globalT("play") : globalT("pause")}
            onClick={() => {
              dispatch(toggleIsPaused());
            }}
            title={state.isPaused ? globalT("play") : globalT("pause")}
          >
            {!state.isPaused ? (
              <PauseIcon className='opacity-20 transition hover:opacity-60' />
            ) : (
              <PlayIcon className='opacity-20 transition hover:opacity-60' />
            )}
          </button>
        </div>
      </div>
      <div className='flex h-[180px]'>
        <div
          className={cn(
            "sb-border-inverse scrollbar-none flex h-full w-2/5 flex-col gap-8 overflow-y-auto border-r p-12",
          )}
        >
          {playingState.blocks.map(block => (
            <div
              className='anim-fade-translate-y-in flex h-12 w-full translate-y-full opacity-0 transition-all'
              key={block.id}
            >
              <div className='flex w-full items-center justify-between gap-8'>
                <span className='font-soundboard sb-text-shadow sb-text-shadow-white truncate text-xs uppercase text-inverse'>
                  {block.name}
                </span>
                {block.looped && (
                  <button
                    className='effect-color flex items-center justify-center rounded-sm bg-none p-4 hover:bg-overlay-light'
                    onClick={() => handleUnloop(block.id)}
                    title={t("loop_button.unloop")}
                  >
                    <RepeatIcon className='sb-color-orange' size={12} />
                    <span className='sr-only'>{t("loop_button.unloop")}</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className={cn("scrollbar-none flex h-full w-3/5 flex-col gap-8 overflow-y-auto p-12")}>
          {playingState.state === "loop-select" && (
            <m.div
              animate={{ opacity: 1 }}
              className='flex h-full w-full flex-col items-center justify-center gap-16'
              initial={{ opacity: 0 }}
              transition={{ delay: 0.2 }}
            >
              <RepeatIcon className='sb-color-orange' size={24} />
              <span className='font-soundboard sb-color-orange f-ui-03 text-xs text-lightest'>
                {t("display.click_pad_to_loop")}
              </span>
            </m.div>
          )}
          {playingState.state === "editing" && (
            <m.div
              animate={{ opacity: 1 }}
              className='flex h-full w-full flex-col items-center justify-center gap-16'
              initial={{ opacity: 0 }}
              transition={{ delay: 0.2 }}
            >
              <EditIcon className='sb-color-orange' size={24} />
              <span className='font-soundboard sb-color-orange f-ui-03 text-xs text-lightest'>
                {t("display.click_pad_to_edit")}
              </span>
            </m.div>
          )}
          {playingState.state === "placeholder" && (
            <m.div
              animate={{ opacity: 1 }}
              className='flex h-full w-full items-center justify-center'
              initial={{ opacity: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className='font-soundboard f-ui-03 text-xs text-lightest'>{t("display.click_pad_to_play")}</span>
            </m.div>
          )}
          {playingState.state === "blocks" &&
            playingState.blocks.map(block => (
              <div
                className='anim-fade-translate-y-in flex h-12 w-full flex-shrink-0 translate-y-full opacity-0 transition-all'
                key={block.id}
              >
                <div className='relative w-full overflow-hidden'>
                  <div className='sb-bg-progress absolute left-0 top-0 flex h-full w-full' />
                  <m.div
                    animate={{ width: `${block.progress * 100}%` }}
                    className='sb-bg-progress absolute left-0 top-0 h-full w-full origin-left border-r-2 border-inverse'
                    transition={{ duration: 0.05 }}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("h-24 w-24", className)}
      fill='none'
      height='24'
      viewBox='0 0 24 24'
      width='24'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path d='M6 4H10V20H6V4Z' fill='white' />
      <path d='M14 4H18V20H14V4Z' fill='white' />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("h-24 w-24", className)}
      fill='white'
      height='24'
      stroke='none'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='2'
      viewBox='0 0 24 24'
      width='24'
      xmlns='http://www.w3.org/2000/svg'
    >
      <polygon points='6 3 20 12 6 21 6 3' />
    </svg>
  );
}
